import "./Login.css";
import { Button, Col, Form, Row } from "react-bootstrap";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useState } from "react";
import axios from "axios";

import AuthBrandPanel from "../components/AuthBrandPanel";
import { clearUser, storeUser } from "../redux/users/usersSlice";
import { getHomePath } from "../utils/accountType";

const ACCENT = "#27b99a";
const BRAND = "#064260";
const MUTED = "#475569";
const SLATE = "#0f172a";

const PASSWORD_PATTERN =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

const UpdatePasswordBusiness = () => {
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userState = useSelector((state) => state?.user);
  const user = userState?.user;

  const clearMessages = () => {
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleBackToSignIn = () => {
    dispatch(clearUser());
    localStorage.clear();
    navigate("/login");
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();

    if (!PASSWORD_PATTERN.test(password)) {
      setErrorMessage("Password does not meet the required criteria.");
      setSuccessMessage("");
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match.");
      setSuccessMessage("");
      return;
    }

    const userId = user?.userId;
    if (!userId) {
      setErrorMessage("Your session has expired. Please sign in again.");
      setSuccessMessage("");
      return;
    }

    setIsLoading(true);
    clearMessages();

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/admin/update-password-business`,
        {
          newPassword: password,
          userId,
        },
      );

      if (response.data.success) {
        const updatedUser = { ...user, is_first_login: false };
        dispatch(storeUser({ user: updatedUser }));
        localStorage.setItem("isFirstLogin", "false");

        setSuccessMessage("Password reset successfully.");
        setTimeout(() => {
          navigate(getHomePath());
        }, 1000);
      } else {
        setErrorMessage("Failed to reset password. Please try again.");
      }
    } catch (error) {
      if (!navigator.onLine) {
        setErrorMessage(
          "Network connection lost. Please check your connection and try again.",
        );
      } else {
        setErrorMessage(
          error.response?.data?.message ||
            "An error occurred. Please try again.",
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Row className="auth-page g-0 min-vh-100">
      <AuthBrandPanel />

      <Col
        className="d-flex align-items-center justify-content-center"
        lg={8}
        md={7}
        style={{ backgroundColor: "#f8fafc", padding: "60px" }}
        xs={12}
      >
        <div className="w-100" style={{ maxWidth: "400px" }}>
          <Form
            className="d-flex flex-column"
            onSubmit={handlePasswordSubmit}
            style={{ gap: "32px" }}
          >
            <div className="d-flex flex-column" style={{ gap: "8px" }}>
              <h2
                className="mb-0"
                style={{ color: SLATE, fontSize: "28px", fontWeight: 600 }}
              >
                Create new password
              </h2>
              <p
                className="mb-0"
                style={{ color: MUTED, fontSize: "14px", lineHeight: 1.4 }}
              >
                Please choose a secure password that you haven&apos;t used
                before.
              </p>
            </div>

            <div className="d-flex flex-column" style={{ gap: "16px" }}>
              <Form.Group
                className="position-relative"
                controlId="new-password"
              >
                <Form.Label
                  className="mb-2"
                  style={{ color: SLATE, fontSize: "14px", fontWeight: 600 }}
                >
                  New Password
                </Form.Label>
                <Form.Control
                  autoComplete="new-password"
                  className="auth-page__input"
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="••••••••••••"
                  style={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    fontSize: "14px",
                    padding: "12px 40px 12px 12px",
                  }}
                  type={showPassword ? "text" : "password"}
                  value={password}
                />
                <Button
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="position-absolute border-0 bg-transparent p-0"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ color: MUTED, right: "12px", top: "42px" }}
                  type="button"
                  variant="link"
                >
                  {showPassword ? (
                    <EyeOff color={MUTED} size={16} strokeWidth={2} />
                  ) : (
                    <Eye color={MUTED} size={16} strokeWidth={2} />
                  )}
                </Button>
                <p
                  className="mb-0 mt-2"
                  style={{ color: MUTED, fontSize: "12px", lineHeight: 1.4 }}
                >
                  Must be at least 8 characters long with one uppercase letter,
                  one lowercase letter, a number, and a symbol.
                </p>
              </Form.Group>

              <Form.Group
                className="position-relative"
                controlId="confirm-password"
              >
                <Form.Label
                  className="mb-2"
                  style={{ color: SLATE, fontSize: "14px", fontWeight: 600 }}
                >
                  Confirm Password
                </Form.Label>
                <Form.Control
                  autoComplete="new-password"
                  className="auth-page__input"
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  placeholder="••••••••••••"
                  style={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    fontSize: "14px",
                    padding: "12px 40px 12px 12px",
                  }}
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                />
                <Button
                  aria-label={
                    showConfirmPassword ? "Hide password" : "Show password"
                  }
                  className="position-absolute border-0 bg-transparent p-0"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{ color: MUTED, right: "12px", top: "42px" }}
                  type="button"
                  variant="link"
                >
                  {showConfirmPassword ? (
                    <EyeOff color={MUTED} size={16} strokeWidth={2} />
                  ) : (
                    <Eye color={MUTED} size={16} strokeWidth={2} />
                  )}
                </Button>
              </Form.Group>
            </div>

            <div
              className="d-flex flex-column align-items-center"
              style={{ gap: "20px" }}
            >
              {errorMessage ? (
                <p
                  className="mb-0 text-center w-100"
                  role="alert"
                  style={{
                    color: "#dc2626",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  {errorMessage}
                </p>
              ) : null}
              {successMessage ? (
                <p
                  className="mb-0 text-center w-100"
                  role="status"
                  style={{ color: ACCENT, fontSize: "14px", fontWeight: 500 }}
                >
                  {successMessage}
                </p>
              ) : null}
              <Button
                className="w-100 border-0"
                disabled={isLoading}
                style={{
                  backgroundColor: BRAND,
                  borderRadius: "8px",
                  fontSize: "16px",
                  fontWeight: 600,
                  padding: "14px",
                }}
                type="submit"
                variant="primary"
              >
                {isLoading ? "Resetting..." : "Reset Password"}
              </Button>
              <button
                onClick={handleBackToSignIn}
                style={{
                  background: "none",
                  border: 0,
                  color: ACCENT,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: "14px",
                  fontWeight: 500,
                  padding: 0,
                  textDecoration: "none",
                }}
                type="button"
              >
                Back to Sign In
              </button>
            </div>
          </Form>
        </div>
      </Col>
    </Row>
  );
};

export default UpdatePasswordBusiness;
